import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Loader2, ShieldAlert, Search, Users, MessageSquare,
  ChevronRight, AlertTriangle, Target, Zap, Eye,
  ArrowRight, Link2, Clock, BarChart3
} from "lucide-react";
import { API } from "@/constants";
import SmartSelect, { CASE_TYPE_OPTIONS } from "@/components/shared/SmartSelect";

const InsightList = ({ items, icon: Icon = ChevronRight, color = "text-blue-500" }) => (
  <div className="space-y-2">
    {(items || []).map((item, i) => (
      <div key={i} className="flex items-start gap-2.5 text-sm">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
        <span className="text-slate-700">{typeof item === "string" ? item : JSON.stringify(item)}</span>
      </div>
    ))}
  </div>
);

const RiskBadge = ({ level }) => {
  const colors = { critical: "bg-red-600", high: "bg-red-500", medium: "bg-amber-500", low: "bg-green-500" };
  return <Badge className={`${colors[level] || "bg-slate-500"} text-white uppercase text-xs`}>{level}</Badge>;
};

const ScoreGauge = ({ score, label, size = "lg" }) => {
  const color = score >= 70 ? "text-red-500" : score >= 40 ? "text-amber-500" : "text-green-500";
  const bg = score >= 70 ? "bg-red-50" : score >= 40 ? "bg-amber-50" : "bg-green-50";
  return (
    <div className={`flex flex-col items-center gap-1 p-4 rounded-2xl ${bg}`}>
      <span className={`${size === "lg" ? "text-4xl" : "text-2xl"} font-black ${color}`}>{score}</span>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
    </div>
  );
};

const ModuleResult = ({ name, data: mod }) => {
  if (!mod || !mod.applicable) return null;
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-700">{name}</p>
          <Badge className={`${mod.score >= 50 ? "bg-red-100 text-red-700" : mod.score >= 20 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
            {mod.score}/100
          </Badge>
        </div>
        <Progress value={mod.score} className="h-1.5 mb-2" />
        {mod.interpretation && <p className="text-xs text-slate-500">{mod.interpretation}</p>}
        {mod.formula && <p className="text-[10px] text-slate-400 mt-1 font-mono">{mod.formula}</p>}
      </CardContent>
    </Card>
  );
};

// ─── Full Fraud Scan ───────────────────────────────────────
const FraudScanTab = () => {
  const [form, setForm] = useState({
    case_type: "", description: "", amounts_text: "15000, 25000, 3500, 12000, 8900, 45000, 7200, 30000, 5000, 22000, 18500, 9000",
    transactions_text: "", parties_text: "", entries_text: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const parseAmounts = (text) => text.split(",").map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
  const parseJSON = (text) => { try { return JSON.parse(text); } catch { return []; } };

  const run = async () => {
    if (!form.case_type) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/fraud/full-scan`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          case_type: form.case_type, description: form.description,
          amounts: parseAmounts(form.amounts_text),
          transactions: parseJSON(form.transactions_text),
          parties: parseJSON(form.parties_text),
          entries: parseJSON(form.entries_text),
        })
      });
      const j = await r.json();
      setData(j.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const mods = data?.modules || {};
  const ai = data?.ai_insights;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><Label className="text-xs font-semibold">Case Type *</Label><div className="mt-1"><SmartSelect value={form.case_type} onValueChange={v => setForm({ ...form, case_type: v })} options={CASE_TYPE_OPTIONS} placeholder="Select case type..." data-testid="fraud-case-type" /></div></div>
        <div><Label className="text-xs font-semibold">Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Suspected billing fraud scheme..." className="mt-1" /></div>
      </div>
      <div>
        <Label className="text-xs font-semibold">Financial Amounts (comma-separated)</Label>
        <Textarea value={form.amounts_text} onChange={e => setForm({ ...form, amounts_text: e.target.value })} placeholder="15000, 25000, 3500..." className="mt-1 min-h-[60px] font-mono text-xs" data-testid="fraud-amounts" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-semibold">Transactions (JSON array)</Label>
          <Textarea value={form.transactions_text} onChange={e => setForm({ ...form, transactions_text: e.target.value })} placeholder='[{"date":"2025-01-15","amount":5000}]' className="mt-1 min-h-[60px] font-mono text-xs" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Parties (JSON array)</Label>
          <Textarea value={form.parties_text} onChange={e => setForm({ ...form, parties_text: e.target.value })} placeholder='[{"name":"John","address":"123 Main","phone":"555-0100"}]' className="mt-1 min-h-[60px] font-mono text-xs" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Entries for Duplicate Check (JSON)</Label>
          <Textarea value={form.entries_text} onChange={e => setForm({ ...form, entries_text: e.target.value })} placeholder='[{"amount":5000,"date":"2025-01-15","description":"consulting"}]' className="mt-1 min-h-[60px] font-mono text-xs" />
        </div>
      </div>
      <Button onClick={run} disabled={loading || !form.case_type} className="w-full bg-red-600 hover:bg-red-700 h-11" data-testid="fraud-scan-btn">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Running Fraud Scan...</> : <><ShieldAlert className="mr-2 h-4 w-4" />Run Full Fraud Scan</>}
      </Button>

      {data && (
        <div className="space-y-5 animate-in fade-in duration-500">
          {/* Composite Score */}
          <div className="rounded-2xl p-6 text-white" style={{ background: data.risk_level === "critical" ? "linear-gradient(135deg, #7F1D1D, #DC2626)" : data.risk_level === "high" ? "linear-gradient(135deg, #92400E, #F59E0B)" : "linear-gradient(135deg, #14532D, #22C55E)" }}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-widest opacity-80">Composite Fraud Score</p>
              <RiskBadge level={data.risk_level} />
            </div>
            <div className="flex items-center justify-center gap-6">
              <p className="text-6xl font-black">{data.composite_score}</p>
              <div className="text-sm opacity-80">
                <p>/ 100</p>
                <p>{data.modules_run} modules analyzed</p>
              </div>
            </div>
          </div>

          {/* Module Results */}
          <div className="grid md:grid-cols-2 gap-3">
            <ModuleResult name="Benford's Law Analysis" data={mods.benford} />
            <ModuleResult name="Round Number Detection" data={mods.round_numbers} />
            <ModuleResult name="Transaction Velocity" data={mods.velocity} />
            <ModuleResult name="Duplicate Detection" data={mods.duplicates} />
            <ModuleResult name="Party Linkage" data={mods.party_linkage} />
          </div>

          {/* Benford Distribution Detail */}
          {mods.benford?.applicable && mods.benford?.actual_distribution && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" />Benford's Law Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-9 gap-1">
                  {Object.entries(mods.benford.actual_distribution).map(([digit, pct]) => (
                    <div key={digit} className="text-center">
                      <div className="h-20 flex items-end justify-center mb-1">
                        <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.min(pct, 100) * 0.8}%` }} />
                      </div>
                      <p className="text-xs font-bold">{digit}</p>
                      <p className="text-[9px] text-slate-500">{pct}%</p>
                      <p className="text-[9px] text-blue-500">{mods.benford.expected_distribution[digit]}%</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center gap-4 mt-2 text-[10px]">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded" />Actual</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-300 rounded" />Expected</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Insights */}
          {ai && (
            <div className="space-y-4">
              {ai.narrative && (
                <Card className="border-0 shadow-sm bg-red-50">
                  <CardContent className="pt-5">
                    <p className="text-sm font-semibold text-red-800 mb-1">Forensic Narrative</p>
                    <p className="text-sm text-red-700">{ai.narrative}</p>
                    {ai.estimated_exposure && <p className="text-sm mt-2 font-semibold text-red-800">Estimated Exposure: {ai.estimated_exposure}</p>}
                  </CardContent>
                </Card>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                {ai.red_flags && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" />Red Flags</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.red_flags} icon={AlertTriangle} color="text-red-500" /></CardContent>
                  </Card>
                )}
                {ai.investigation_steps && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Search className="h-4 w-4 text-blue-600" />Investigation Steps</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.investigation_steps} color="text-blue-500" /></CardContent>
                  </Card>
                )}
                {ai.evidence_to_preserve && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Eye className="h-4 w-4 text-amber-600" />Evidence to Preserve</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.evidence_to_preserve} icon={Eye} color="text-amber-500" /></CardContent>
                  </Card>
                )}
                {ai.legal_theories && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="h-4 w-4 text-purple-600" />Legal Theories</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.legal_theories} color="text-purple-500" /></CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Communication Analysis ────────────────────────────────
const CommTab = () => {
  const [form, setForm] = useState({
    parties_text: '[{"name":"John Smith","role":"Plaintiff"},{"name":"Jane Doe","role":"Defendant"},{"name":"Acme Corp","role":"Entity"}]',
    comms_text: '[{"from":"John Smith","to":"Jane Doe","hour":22,"day_of_week":"Saturday"},{"from":"Jane Doe","to":"Acme Corp","hour":14,"day_of_week":"Monday"},{"from":"John Smith","to":"Acme Corp","hour":23,"day_of_week":"Sunday"},{"from":"John Smith","to":"Jane Doe","hour":3,"day_of_week":"Wednesday"},{"from":"Jane Doe","to":"John Smith","hour":10,"day_of_week":"Tuesday"}]',
    description: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    let parties, comms;
    try { parties = JSON.parse(form.parties_text); } catch { return; }
    try { comms = JSON.parse(form.comms_text); } catch { return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/fraud/communication-analysis`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parties, communications: comms, case_description: form.description })
      });
      const j = await r.json();
      setData(j.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const ai = data?.ai_analysis;

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs font-semibold">Parties (JSON array)</Label>
        <Textarea value={form.parties_text} onChange={e => setForm({ ...form, parties_text: e.target.value })} className="mt-1 min-h-[60px] font-mono text-xs" data-testid="comm-parties" />
      </div>
      <div>
        <Label className="text-xs font-semibold">Communications (JSON array with from, to, hour, day_of_week)</Label>
        <Textarea value={form.comms_text} onChange={e => setForm({ ...form, comms_text: e.target.value })} className="mt-1 min-h-[80px] font-mono text-xs" data-testid="comm-data" />
      </div>
      <div><Label className="text-xs font-semibold">Case Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Suspected collusion between..." className="mt-1" /></div>
      <Button onClick={run} disabled={loading} className="w-full bg-violet-600 hover:bg-violet-700 h-11" data-testid="comm-analyze-btn">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Analyzing Communications...</> : <><MessageSquare className="mr-2 h-4 w-4" />Analyze Communication Patterns</>}
      </Button>

      {data && (
        <div className="space-y-5 animate-in fade-in duration-500">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ScoreGauge score={data.composite_score} label="Composite" />
            <ScoreGauge score={data.frequency_anomaly_score} label="Frequency" size="sm" />
            <ScoreGauge score={data.unusual_hours_score} label="Timing" size="sm" />
            <div className="flex flex-col items-center gap-1 p-4 rounded-2xl bg-slate-50">
              <span className="text-2xl font-black text-slate-700">{data.total_communications}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Comms</span>
            </div>
          </div>

          {/* Timing Breakdown */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-violet-600" />Timing Analysis</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-xl bg-green-50"><p className="text-lg font-bold text-green-700">{data.timing_analysis?.business_hours || 0}</p><p className="text-xs text-slate-500">Business Hours</p></div>
                <div className="p-3 rounded-xl bg-amber-50"><p className="text-lg font-bold text-amber-700">{data.timing_analysis?.after_hours || 0}</p><p className="text-xs text-slate-500">After Hours ({data.after_hours_percentage}%)</p></div>
                <div className="p-3 rounded-xl bg-red-50"><p className="text-lg font-bold text-red-700">{data.timing_analysis?.weekend || 0}</p><p className="text-xs text-slate-500">Weekend ({data.weekend_percentage}%)</p></div>
              </div>
            </CardContent>
          </Card>

          {/* Communication Matrix */}
          {data.communication_matrix && Object.keys(data.communication_matrix).length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Link2 className="h-4 w-4 text-blue-600" />Communication Matrix</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(data.communication_matrix).sort((a, b) => b[1] - a[1]).map(([channel, count], i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                      <span className="text-sm font-medium text-slate-700">{channel}</span>
                      <Badge variant="outline">{count} messages</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Formulas */}
          {data.formulas && (
            <Card className="border-0 shadow-sm bg-slate-50">
              <CardContent className="pt-4">
                <p className="text-xs font-semibold text-slate-600 mb-2">Formulas Used</p>
                <div className="space-y-1 font-mono text-[11px] text-slate-500">
                  {Object.entries(data.formulas).map(([k, v]) => <p key={k}>{k}: {v}</p>)}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Insights */}
          {ai && (
            <div className="grid md:grid-cols-2 gap-4">
              {ai.suspicious_patterns && (
                <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" />Suspicious Patterns</CardTitle></CardHeader>
                  <CardContent><InsightList items={ai.suspicious_patterns} icon={AlertTriangle} color="text-red-500" /></CardContent>
                </Card>
              )}
              {ai.recommended_subpoenas && (
                <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-blue-600" />Recommended Subpoenas</CardTitle></CardHeader>
                  <CardContent><InsightList items={ai.recommended_subpoenas} color="text-blue-500" /></CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Party Network ─────────────────────────────────────────
const NetworkTab = () => {
  const [form, setForm] = useState({
    parties_text: '[{"name":"John Smith","role":"Plaintiff","address":"123 Main St","phone":"555-0100","email":"john@acme.com","entity":"Acme Corp"},{"name":"Jane Doe","role":"Defendant","address":"123 Main St","phone":"555-0200","email":"jane@acme.com","entity":"Acme Corp"},{"name":"Bob Wilson","role":"Witness","address":"456 Oak Ave","phone":"555-0100","email":"bob@other.com","entity":"Beta LLC"}]',
    description: ""
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    let parties;
    try { parties = JSON.parse(form.parties_text); } catch { return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/fraud/party-network`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parties, case_description: form.description })
      });
      const j = await r.json();
      setData(j.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const linkage = data?.linkage_analysis;
  const ai = data?.ai_analysis;

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-xs font-semibold">Parties (JSON array with name, role, address, phone, email, entity)</Label>
        <Textarea value={form.parties_text} onChange={e => setForm({ ...form, parties_text: e.target.value })} className="mt-1 min-h-[100px] font-mono text-xs" data-testid="network-parties" />
      </div>
      <div><Label className="text-xs font-semibold">Case Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Investigating connections between..." className="mt-1" /></div>
      <Button onClick={run} disabled={loading} className="w-full bg-slate-800 hover:bg-slate-700 h-11" data-testid="network-analyze-btn">
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Mapping Network...</> : <><Users className="mr-2 h-4 w-4" />Analyze Party Network</>}
      </Button>

      {data && (
        <div className="space-y-5 animate-in fade-in duration-500">
          <div className="grid grid-cols-3 gap-3">
            <ScoreGauge score={linkage?.score || 0} label="Linkage Score" />
            <div className="flex flex-col items-center gap-1 p-4 rounded-2xl bg-blue-50">
              <span className="text-3xl font-black text-blue-700">{linkage?.links_found || 0}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Links Found</span>
            </div>
            <div className="flex flex-col items-center gap-1 p-4 rounded-2xl bg-slate-50">
              <span className="text-3xl font-black text-slate-700">{data.parties_count || 0}</span>
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Parties</span>
            </div>
          </div>

          {/* Links Detail */}
          {linkage?.links && linkage.links.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Link2 className="h-4 w-4 text-red-600" />Discovered Links</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {linkage.links.map((link, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-100 text-red-700 text-[10px]">{link.type.replace(/_/g, " ")}</Badge>
                          <span className="text-sm font-medium truncate">{link.parties.join(" & ")}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{link.value}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 ml-2">Weight: {link.weight}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Analysis */}
          {ai && (
            <div className="space-y-4">
              {ai.network_diagram_description && (
                <Card className="border-0 shadow-sm bg-slate-900 text-white">
                  <CardContent className="pt-5">
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Network Map</p>
                    <p className="text-sm text-slate-200">{ai.network_diagram_description}</p>
                  </CardContent>
                </Card>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                {ai.hidden_connections && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-red-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Eye className="h-4 w-4 text-red-600" />Hidden Connections</CardTitle></CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {ai.hidden_connections.map((c, i) => (
                          <div key={i} className="p-2.5 rounded-lg bg-red-50">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-red-100 text-red-700 text-[10px]">{c.risk || "HIGH"}</Badge>
                              <span className="text-xs font-semibold text-slate-700">{c.connection_type}</span>
                            </div>
                            <p className="text-xs text-slate-600">{c.evidence}</p>
                            {c.parties && <p className="text-[10px] text-slate-500 mt-0.5">Parties: {c.parties.join(", ")}</p>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                {ai.investigation_recommendations && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-blue-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-blue-600" />Recommendations</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.investigation_recommendations} color="text-blue-500" /></CardContent>
                  </Card>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {ai.shell_company_indicators && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Shell Company Indicators</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.shell_company_indicators} icon={AlertTriangle} color="text-amber-500" /></CardContent>
                  </Card>
                )}
                {ai.beneficial_ownership_concerns && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Beneficial Ownership</CardTitle></CardHeader>
                    <CardContent><InsightList items={ai.beneficial_ownership_concerns} color="text-purple-500" /></CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────
const FraudAnalyticsPage = () => {
  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-6" data-testid="fraud-analytics">
      <div className="rounded-2xl p-6 text-white" style={{ background: "linear-gradient(135deg, #7F1D1D, #B91C1C)" }}>
        <div className="flex items-center gap-3 mb-1">
          <ShieldAlert className="h-7 w-7 text-red-300" />
          <h1 className="text-2xl font-black tracking-tight">Fraud Analytics</h1>
          <Badge className="bg-red-500/20 text-red-200 border-red-500/30">FORENSIC</Badge>
        </div>
        <p className="text-red-200 text-sm">Benford's Law, velocity analysis, party linkage & AI-powered fraud detection</p>
      </div>
      <Tabs defaultValue="scan" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 h-auto p-1">
          <TabsTrigger value="scan" className="text-xs py-2 flex flex-col gap-0.5 data-[state=active]:bg-red-600 data-[state=active]:text-white"><ShieldAlert className="h-3.5 w-3.5" />Full Scan</TabsTrigger>
          <TabsTrigger value="comms" className="text-xs py-2 flex flex-col gap-0.5 data-[state=active]:bg-violet-600 data-[state=active]:text-white"><MessageSquare className="h-3.5 w-3.5" />Communications</TabsTrigger>
          <TabsTrigger value="network" className="text-xs py-2 flex flex-col gap-0.5 data-[state=active]:bg-slate-800 data-[state=active]:text-white"><Users className="h-3.5 w-3.5" />Party Network</TabsTrigger>
        </TabsList>
        <TabsContent value="scan"><FraudScanTab /></TabsContent>
        <TabsContent value="comms"><CommTab /></TabsContent>
        <TabsContent value="network"><NetworkTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default FraudAnalyticsPage;
